/**
 * Competition detail layout decisions (Bez, 2026-10-03, competition #0 on Stage: "the chart card is tall on the left while the
 * right card leaves a tall empty column"). Layout-symmetry rule: twins stay symmetrical, no empty columns, state toggles
 * reserve space. Pure, unit-tested; Detail.tsx only renders them.
 */
export type TradingLayout = 'side' | 'stack'

/**
 * Live with the full trade form (wallet connected, rules accepted): the form is taller than a laptop screen, so it stands
 * beside the chart and the history/rules stack under the chart. Any other case (another phase, or the live panel still
 * showing its short gate) the chart takes the full width and the trade and rules cards stand as equal-height twins under
 * it. Nothing is ever left as an empty column (Stage r106 at 1440/1710: the gated panel left ~350 px empty beside the history).
 */
export const tradingLayout = (phase: string, fullPanel = false): TradingLayout => (phase === 'live' && fullPanel ? 'side' : 'stack')

/** The date next to the countdown in the header; null when the countdown already says it ("Ended <date>"). */
export function timeCaption(phase: string, startTime: number, endTime: number, fmt: (t: number) => string): string | null {
  if (phase === 'upcoming') return `Starts ${fmt(startTime)}`
  if (phase === 'live') return `Ends ${fmt(endTime)}`
  if (phase === 'awaiting_result' || phase === 'refundable') return `Ended ${fmt(endTime)}`
  return null
}

export interface StatusInput {
  phase: string
  wallet?: string | null
  rank?: number | null
  won?: boolean
  claimed?: boolean
  ackDone?: boolean
  winners: number
}
export type StatusTone = 'subtle' | 'success' | 'text'

/** "Your status" in one line, for every phase, with and without a wallet. */
export function yourStatus(s: StatusInput): { text: string; tone: StatusTone } {
  if (!s.wallet) return { text: 'Connect a wallet to see your rank here.', tone: 'subtle' }
  if (s.phase === 'upcoming') {
    return s.ackDone
      ? { text: 'Rules accepted. Your first trade through the pool after the start enters you.', tone: 'success' }
      : { text: 'Accept the rules now and the trade panel opens for you at the start.', tone: 'text' }
  }
  if (s.phase === 'live') {
    if (s.rank) return s.rank <= s.winners ? { text: `You are #${s.rank}, in the prize.`, tone: 'success' } : { text: `You are #${s.rank}. The top ${s.winners} share the prize.`, tone: 'text' }
    return { text: 'No eligible trade yet. Trade through the pool to enter.', tone: 'subtle' }
  }
  if (s.won) return s.claimed ? { text: 'You won. Reward claimed.', tone: 'success' } : { text: 'You won. Claim your reward in the header.', tone: 'success' }
  if (s.phase === 'awaiting_result') return s.rank ? { text: `You are #${s.rank} in the provisional result.`, tone: 'text' } : { text: 'No eligible trade from this wallet. The result is being verified.', tone: 'subtle' }
  if (s.rank) return { text: `You finished #${s.rank}.`, tone: 'text' }
  return { text: 'This wallet did not place in this competition.', tone: 'subtle' }
}
